import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { generatePackageFinderShareContent } from '../utils/packageFinderShare';
import { loadCategoriesFromServer, Category } from '../utils/categories';
import { loadServicesFromServer } from '../utils/ServiceAPI';
import { Package } from '../types/Package';
import ShareButton from '../components/Sharing/ShareButton';
import SEOHead from '../components/SEO/SEOHead';
import { useWebsiteContent } from '../hooks/useWebsiteContent';
import InquiryDatePicker from '../components/Common/InquiryDatePicker';
import { useInquiryDate, withInquiryDate } from '../utils/inquiryDate';
import { preferredDateLine } from '../utils/preferredDate';
import { LOCAL_BUSINESS, contactLocalHtml, whatsappHref } from '../utils/localBusiness';

const ContactPage: React.FC<{ requestQuote?: boolean }> = ({ requestQuote = false }) => {
  const websiteContent = useWebsiteContent();
  const [inquiryDate] = useInquiryDate(true);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [packages, setPackages] = useState<Package[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Load categories and packages when modal opens
  useEffect(() => {
    if (showCategoryModal) {
      const loadData = async () => {
        setLoadingCategories(true);
        try {
          const [loadedCategories, loadedPackages] = await Promise.all([
            loadCategoriesFromServer(),
            loadServicesFromServer()
          ]);
          setCategories(loadedCategories);
          setPackages(loadedPackages);
        } catch (error) {
          console.error('Error loading categories or packages:', error);
        } finally {
          setLoadingCategories(false);
        }
      };
      loadData();
    }
  }, [showCategoryModal]);

  // Get categories that have available packages
  const getCategoriesWithPackages = (): Category[] => {
    // First filter to only available packages
    const availablePackages = packages.filter(pkg => pkg.available !== false);
    
    return categories.filter(category => {
      return availablePackages.some(pkg => {
        // Check categories array
        if (pkg.categories && pkg.categories.length > 0) {
          return pkg.categories.some(catSlug => 
            catSlug.toLowerCase() === category.slug.toLowerCase()
          );
        }
        // Check single category field
        if (pkg.category) {
          return pkg.category.toLowerCase() === category.name.toLowerCase() ||
                 pkg.category.toLowerCase() === category.slug.toLowerCase();
        }
        return false;
      });
    });
  };

  const handleShareWithCategory = (category: Category) => {
    // Generate share content for the category
    const shareContent = generatePackageFinderShareContent({ 
      open: true, 
      category: category.name 
    });
    
    // Return share content to be used by ShareButton
    return shareContent;
  };

  return (
    <div className="contact-page" data-track-source={requestQuote ? 'quote_page' : 'contact'}>
      <SEOHead
        title={requestQuote ? 'Request a Quote' : 'Contact - Get in Touch'}
        description={requestQuote
          ? 'Request a photography quote from Carmen Lorraine in Cape Town. Tell her about the session and she will reply with availability and a quotation.'
          : 'Ready to capture your story? Get in touch with Carmen Lorraine Photography to discuss your photography needs. Based in Cape Town, South Africa.'}
        keywords={['contact photographer', 'Cape Town photographer contact', 'photography inquiry', 'book photographer', 'photography consultation']}
        type="website"
      />
      <main className="page-content">
        <section className="contact-section public-contact">
          <div className="contact-content">
            <h1>{requestQuote ? 'Request a quote' : "Let's Create Something Beautiful Together"}</h1>
            <p className="contact-intro">{requestQuote
              ? 'Tell Carmen about the session you have in mind. She will reply with availability and a quotation.'
              : 'Ready to capture your story? Get in touch to discuss your photography needs.'}</p>
            <InquiryDatePicker />
            <ul className="contact-details">
              <li className="contact-row">
                <i className="fas fa-envelope" aria-hidden="true"></i>
                <a 
                  href={`mailto:${LOCAL_BUSINESS.email}?subject=Photography Inquiry&body=Hi Carmen,%0D%0A%0D%0AI found your website and I'm interested in your photography services.%0D%0A%0D%0ACould you please provide more information about:%0D%0A- Your packages and pricing%0D%0A- Available dates%0D%0A- What's included in your services%0D%0A%0D%0A${encodeURIComponent(preferredDateLine(inquiryDate))}%0D%0A%0D%0AThank you!%0D%0A%0D%0ABest regards,`}
                  className="contact-email"
                >
                  {LOCAL_BUSINESS.email}
                </a>
              </li>
              <li className="contact-row">
                <i className="fas fa-phone" aria-hidden="true"></i>
                <a 
                  href={LOCAL_BUSINESS.phoneTel}
                  className="contact-phone"
                >
                  {LOCAL_BUSINESS.phoneDisplay}
                </a>
              </li>
              <li className="contact-row">
                <i className="fas fa-map-marker-alt" aria-hidden="true"></i>
                <span className="contact-location">{websiteContent.contactLocation}</span>
              </li>
            </ul>
            {!requestQuote && (
              <div dangerouslySetInnerHTML={{ __html: contactLocalHtml(websiteContent) }} />
            )}
            <div className="contact-actions">
              <a
                href={whatsappHref(websiteContent.contactPhone)}
                className="contact-whatsapp"
                target="_blank"
                rel="noopener noreferrer"
              >
                  <i className="fab fa-whatsapp" aria-hidden="true"></i>
                  <span>Message on WhatsApp</span>
                </a>
              <div className="contact-secondary">
                <ShareButton
                  content={generatePackageFinderShareContent()}
                  variant="text"
                  size="medium"
                  platforms={['whatsapp', 'twitter', 'facebook', 'instagram', 'email']}
                  showCopyButton={true}
                  className="contact-share-button"
                />
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Category Selection Modal */}
      {showCategoryModal && createPortal(
        <div 
          className="category-selection-modal-overlay"
          onClick={() => setShowCategoryModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
        >
          <div 
            className="category-selection-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>Select a Category</h2>
              <button
                onClick={() => setShowCategoryModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '0.5rem'
                }}
              >
                ×
              </button>
            </div>
            
            {loadingCategories ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <p>Loading categories...</p>
              </div>
            ) : (
              <>
                <p style={{ marginBottom: '1.5rem', color: '#666' }}>
                  Choose a category to share. If the category has no packages, contact options will be shown instead.
                </p>
                
                {getCategoriesWithPackages().length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <p style={{ color: '#666', marginBottom: '1rem' }}>No categories with packages found.</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      <a
                        href={whatsappHref(websiteContent.contactPhone, withInquiryDate("Hi! I'm interested in your photography services. Could you please provide more information?"))}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          background: '#25D366',
                          color: 'white',
                          border: 'none',
                          padding: '0.75rem 1.5rem',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '1rem',
                          fontWeight: 500,
                          textDecoration: 'none',
                          display: 'inline-block',
                          textAlign: 'center'
                        }}
                      >
                        <i className="fab fa-whatsapp" style={{ marginRight: '0.5rem' }}></i>
                        Contact via WhatsApp
                      </a>
                      <a
                        href={`mailto:${websiteContent.contactEmail}?subject=${encodeURIComponent('Photography Inquiry')}&body=${encodeURIComponent(withInquiryDate("Hi! I'm interested in your photography services."))}`}
                        style={{
                          background: '#8B4513',
                          color: 'white',
                          border: 'none',
                          padding: '0.75rem 1.5rem',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '1rem',
                          fontWeight: 500,
                          textDecoration: 'none',
                          display: 'inline-block',
                          textAlign: 'center'
                        }}
                      >
                        <i className="fas fa-envelope" style={{ marginRight: '0.5rem' }}></i>
                        Contact via Email
                      </a>
                      <a
                        href={`tel:${websiteContent.contactPhone.replace(/\s+/g, '')}`}
                        style={{
                          background: '#666',
                          color: 'white',
                          border: 'none',
                          padding: '0.75rem 1.5rem',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '1rem',
                          fontWeight: 500,
                          textDecoration: 'none',
                          display: 'inline-block',
                          textAlign: 'center'
                        }}
                      >
                        <i className="fas fa-phone" style={{ marginRight: '0.5rem' }}></i>
                        Call {websiteContent.contactPhone}
                      </a>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: '0.75rem' }}>
                    {getCategoriesWithPackages().map(category => {
                      // Only count available packages
                      const packageCount = packages.filter(pkg => {
                        if (pkg.available === false) return false; // Explicitly exclude unavailable
                        if (pkg.categories && pkg.categories.length > 0) {
                          return pkg.categories.some(catSlug => 
                            catSlug.toLowerCase() === category.slug.toLowerCase()
                          );
                        }
                        if (pkg.category) {
                          return pkg.category.toLowerCase() === category.name.toLowerCase() ||
                                 pkg.category.toLowerCase() === category.slug.toLowerCase();
                        }
                        return false;
                      }).length;

                      const categoryShareContent = handleShareWithCategory(category);
                      
                      return (
                        <div
                          key={category.id}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '1rem',
                            border: `2px solid ${category.color || '#8B4513'}`,
                            borderRadius: '8px',
                            background: 'white',
                            textAlign: 'left'
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: '1.1rem', marginBottom: '0.25rem' }}>
                              {category.name}
                            </div>
                            <div style={{ fontSize: '0.875rem', opacity: 0.7 }}>
                              {packageCount} package{packageCount !== 1 ? 's' : ''} available
                            </div>
                          </div>
                          <div style={{ marginLeft: '1rem' }}>
                            <ShareButton
                              content={categoryShareContent}
                              variant="icon"
                              size="small"
                              platforms={['whatsapp', 'twitter', 'facebook', 'instagram', 'email']}
                              showCopyButton={true}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ContactPage;

